import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { YtdCard } from "@/components/YtdCard";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Landmark, Save, Camera, ExternalLink } from "lucide-react";
import { MyYearSummaryCard } from "@/components/MyYearSummaryCard";
import { toast } from "sonner";
import { fmtDate } from "@/components/DatePicker";
import { MyChangeRequests } from "@/components/MyChangeRequests";
import { W4Card } from "@/components/W4Card";
import { I9Card } from "@/components/I9Card";
import { MyDocumentsCard } from "@/components/MyDocumentsCard";
import { AddressFields, parseAddress, formatAddress } from "@/components/AddressFields";

function toDataUrl(file, max = 256) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { const s = Math.min(1, max / Math.max(img.width, img.height)); const c = document.createElement("canvas"); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s); c.getContext("2d").drawImage(img, 0, 0, c.width, c.height); resolve(c.toDataURL("image/jpeg", 0.85)); };
    img.src = URL.createObjectURL(file);
  });
}

const FIELDS = [["preferred_name", "Preferred name"], ["phone", "Mobile phone"], ["personal_email", "Personal email"], ["address", "Home address"], ["emergency_contact_name", "Emergency contact name"], ["emergency_contact_phone", "Emergency contact phone"]];

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [f, setF] = useState({});
  const [dd, setDd] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/employee").then((r) => { setEmp(r.data); setF({ ...Object.fromEntries(FIELDS.map(([k]) => [k, r.data[k] || ""])), address_parts: r.data.address_parts || null }); }).catch(() => {});
    api.get("/direct-deposit/mine").then((r) => setDd(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/me/employee", f); setEmp(data); toast.success("Your information was updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const photo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const avatar = await toDataUrl(file);
    try { const { data } = await api.put("/me/employee", { avatar }); setEmp(data); setUser?.({ ...user, avatar }); toast.success("Photo updated"); } catch { toast.error("Photo upload failed"); }
  };
  const openDD = async () => {
    try { const { data } = await api.post("/direct-deposit/self"); window.open(data.link, "_blank", "noopener"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!emp) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="my-profile-page">
      <div><h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">My Information</h1><p className="text-sm text-muted-foreground mt-1">Keep your contact details current. Pay and job details are managed by HR.</p></div>

      <Card className="p-5 flex items-center gap-4 flex-wrap">
        <Avatar className="w-20 h-20"><AvatarImage src={emp.avatar} /><AvatarFallback>{emp.name?.[0]}</AvatarFallback></Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg">{emp.name}</div>
          <div className="text-sm text-muted-foreground">{emp.job_title} · {emp.department} · {emp.email}</div>
          <div className="text-xs text-muted-foreground mt-1">Clock PIN: <span className="font-mono-num tracking-widest">{emp.pin}</span></div>
        </div>
        <label className="cursor-pointer"><span className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-border hover:bg-accent"><Camera className="w-4 h-4" /> Change photo</span><input data-testid="profile-photo-input" type="file" accept="image/*" className="hidden" onChange={photo} /></label>
      </Card>

      <YtdCard employeeId={emp.id} />

      <Card className="p-5 space-y-4" data-testid="profile-form">
        <h2 className="font-display font-semibold text-base md:text-lg">Contact & emergency details</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.filter(([k]) => k !== "address").map(([k, label]) => (
            <div key={k} className="space-y-1"><Label className="text-xs">{label}</Label>{k.endsWith("phone") ? <PhoneInput data-testid={`profile-${k}`} value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })} /> : <Input data-testid={`profile-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}</div>
          ))}
          <AddressFields testid="profile-addr" label="Home address" value={f.address_parts || parseAddress(f.address)} onChange={(a) => setF({ ...f, address_parts: a, address: formatAddress(a) })} />
        </div>
        <div className="flex justify-end"><Button data-testid="profile-save-btn" onClick={save} disabled={saving}><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}</Button></div>
      </Card>

      <W4Card employeeName={emp.name} />
      <I9Card employeeName={emp.name} />
      <MyDocumentsCard />

      <Card className="p-5" data-testid="profile-dd-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-base md:text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-primary" /> Direct deposit</h2>
            {dd ? <p className="text-sm text-muted-foreground mt-1">On file since {fmtDate(dd.completed_at)}: {dd.accounts.map((a) => `${a.bank_name} ····${a.account_last4}`).join(", ")}</p>
              : <p className="text-sm text-muted-foreground mt-1">No direct deposit authorization on file.</p>}
          </div>
          <Button variant="outline" data-testid="profile-dd-btn" onClick={openDD}><ExternalLink className="w-4 h-4 mr-1.5" /> {dd ? "Update bank account" : "Set up direct deposit"}</Button>
        </div>
      </Card>

      <MyChangeRequests />
      <MyYearSummaryCard />
    </div>
  );
}
